import { withTag } from "@kidd-cli/utils/tag";
import { err, ok, toError } from "@kidd-cli/utils/fp";
import { loadConfig as loadConfig$1 } from "c12";
import { attemptAsync } from "es-toolkit";
import { validate } from "@kidd-cli/utils/validate";
import { z } from "zod";
//#region src/utils/compile.ts
/**
* All supported cross-compilation targets with human-readable labels.
*
* This is the single source of truth — the {@link CompileTarget} union type
* and the Zod validation schema are both derived from this array.
*
* Targets marked `default: true` are compiled when no explicit targets are configured,
* covering ~95% of developer environments.
*/
const compileTargets = [
	{
		target: "darwin-arm64",
		label: "macOS Apple Silicon",
		default: true
	},
	{
		target: "darwin-x64",
		label: "macOS Intel",
		default: true
	},
	{
		target: "linux-arm64",
		label: "Linux ARM64",
		default: false
	},
	{
		target: "linux-x64",
		label: "Linux x64",
		default: true
	},
	{
		target: "linux-x64-musl",
		label: "Linux x64 (musl)",
		default: false
	},
	{
		target: "windows-arm64",
		label: "Windows ARM64",
		default: false
	},
	{
		target: "windows-x64",
		label: "Windows x64",
		default: true
	}
];
//#endregion
//#region src/utils/schema.ts
/**
* @private
*/
const compileTargetValues = compileTargets.map((entry) => entry.target);
/**
* @private
*/
const CompileTargetSchema = z.enum(compileTargetValues);
/**
* @private
*/
const BuildOptionsSchema = z.object({
	clean: z.boolean().optional(),
	define: z.record(z.string(), z.string()).optional(),
	external: z.array(z.string()).optional(),
	minify: z.boolean().optional(),
	out: z.string().optional(),
	sourcemap: z.boolean().optional(),
	target: z.string().optional()
}).strict();
/**
* @private
*/
const CompileOptionsSchema = z.object({
	autoloadDotenv: z.boolean().optional(),
	name: z.string().optional(),
	out: z.string().optional(),
	targets: z.array(CompileTargetSchema).optional()
}).strict();
/**
* Zod schema validating the {@link KiddConfig} shape.
*/
const KiddConfigSchema = z.object({
	build: BuildOptionsSchema.optional(),
	commands: z.string().optional(),
	compile: z.union([z.boolean(), CompileOptionsSchema]).optional(),
	entry: z.string().optional(),
	include: z.array(z.string()).optional()
}).strict();
/**
* Validate arbitrary data against the {@link KiddConfigSchema}.
*
* @param data - The unknown value to validate.
* @returns A Result tuple - `[null, KiddConfig]` on success or `[Error, null]` on failure.
*/
function validateConfig(data) {
	return validate({
		schema: KiddConfigSchema,
		params: data,
		createError: ({ message }) => /* @__PURE__ */ new Error(`Invalid kidd config:\n  ${message}`)
	});
}
//#endregion
//#region src/utils/loader.ts
/**
* Load and validate a `kidd.config.ts` file using c12.
*
* Searches for a config file named `kidd` (e.g. `kidd.config.ts`, `kidd.config.mts`)
* in the given working directory, validates it against the build config schema,
* and returns a tagged config object.
*
* @param options - Optional loader configuration.
* @returns A Result tuple - `[null, LoadConfigResult]` on success or `[Error, null]` on failure.
*/
async function loadConfig(options) {
	const { cwd, defaults, overrides } = options ?? {};
	const [loadError, loaded] = await attemptAsync(() => loadConfig$1({
		cwd,
		defaults,
		name: "kidd",
		overrides
	}));
	if (loadError) return err(`Failed to load kidd config: ${toError(loadError).message}`);
	if (!loaded) return err("Failed to load kidd config: no configuration was returned");
	const [validateError, config] = validateConfig(loaded.config);
	if (validateError) return err(validateError);
	return ok({
		config: withTag(config, "KiddConfig"),
		configFile: loaded.configFile
	});
}
//#endregion
export { KiddConfigSchema, compileTargets, loadConfig, validateConfig };

//# sourceMappingURL=utils.js.map